// src/config/CountryKeyManager.ts

// Allowed country codes
export type CountryCode = 'ZA' | 'FI' | 'NO' | 'US';

// Type for payment keys (key-value pairs like VIPPS, NETS, etc.)
export type AuthKeys = Record<string, string>;

// Enum to represent supported brands
export enum BrandIdentifier {
  Flexistore = 'Flexistore',
  StorPro = 'StorPro',
}

/**
 * CountryKeyManager is a singleton class that loads
 * and provides payment keys specific to a brand and country.
 * 
 * - Automatically sets brand based on country.
 * - Only includes keys relevant to the selected country + brand.
 * - Safe fallback if keys are missing (returns empty object).
 */
export class CountryKeyManager {
  // Singleton instance
  private static _instance: CountryKeyManager;

  // Current brand (default: Flexistore)
  private _brand: BrandIdentifier = BrandIdentifier.Flexistore;

  // Current country (default: ZA)
  private _country: CountryCode = 'ZA';

  // Stores the loaded keys for the current brand + country
  private _keys: AuthKeys = {};

  // Private constructor to enforce singleton
  private constructor() {}

  /**
   * Returns the singleton instance of CountryKeyManager
   */
  public static getInstance(): CountryKeyManager {
    if (!this._instance) {
      this._instance = new CountryKeyManager();
    }
    return this._instance;
  }

  /**
   * Initializes the manager with a country
   * Automatically sets the brand based on country
   * @param country - country code ('ZA', 'FI', 'NO', 'US')
   */
  public initialize(country: string) {
    this._country = (['ZA','FI','NO','US'].includes(country.toUpperCase()) ? country.toUpperCase() : 'ZA') as CountryCode;

    const brandFromEnv = import.meta.env.VITE_BRAND_IDENTIFIER as string | undefined;
    if (!brandFromEnv) {
      throw new Error("VITE_BRAND_IDENTIFIER is not set. Please set it in your environment!");
    }
    if (!Object.values(BrandIdentifier).includes(brandFromEnv as BrandIdentifier)) {
      throw new Error(`VITE_BRAND_IDENTIFIER=${brandFromEnv} is invalid. Must be 'Flexistore' or 'StorPro'.`);
    }

    this._brand = brandFromEnv as BrandIdentifier;

    this._loadKeys();
  }
  /**
   * Loads keys from environment variables based on country
   * Only loads keys that are relevant for the current country
   */
  private _loadKeys() {
    this._keys = {};

    // Always include base API URL (required)
    const baseApiUrl = import.meta.env.VITE_BASE_API_URL;
    if (!baseApiUrl) {
      throw new Error(`Missing required environment key: VITE_BASE_API_URL for ${this._country}`);
    }
    this._keys['BASE_API_URL'] = baseApiUrl;

    // Country-specific payment provider keys
    switch (this._country) {
      case 'NO':
        // Norway supports all three: Vipps and Nets
        this._addVippsKeys();
        this._addNetsKeys();
        break;
        
      case 'FI':
        // Finland supports Nets
        this._addNetsKeys();
        break;
        
      case 'ZA':
        // South Africa does not support any external payment provider
        break;
        
      case 'US':
        // US (StorPro) does not support any external payment provider
        break;
      default:
        
        
    }
  }

  /**
   * Adds Vipps keys if they exist in environment
   */
  private _addVippsKeys() {
    const vippsKeys = {
      'VIPPS_API_URL_BASE': import.meta.env.VITE_VIPPS_API_URL_BASE,
      'VIPPS_API_URL': import.meta.env.VITE_VIPPS_API_URL,
      'VIPPS_API_OPENID_CONFIG_URL': import.meta.env.VITE_VIPPS_API_OPENID_CONFIG_URL,
      'VIPPS_CLIENT_ID': import.meta.env.VITE_VIPPS_CLIENT_ID,
      'VIPPS_CLIENT_SECRET': import.meta.env.VITE_VIPPS_CLIENT_SECRET,
      'VIPPS_TOKEN_ENDPOINT_AUTH_METHOD': import.meta.env.VITE_VIPPS_TOKEN_ENDPOINT_AUTH_METHOD,
      'VIPPS_REDIRECT_URI': import.meta.env.VITE_VIPPS_REDIRECT_URI,
    };

    // Only add keys that actually exist
    Object.entries(vippsKeys).forEach(([key, value]) => {
      if (value) {
        this._keys[key] = value;
      }
    });
  }

  /**
   * Adds Nets keys if they exist in environment
   */
  private _addNetsKeys() {
    const netsKeys = {
      'NETS_ID_REDIRECT_URI': import.meta.env.VITE_NETS_ID_REDIRECT_URI,
      'NETS_API_OPENID_CONFIG_URL': import.meta.env.VITE_NETS_API_OPENID_CONFIG_URL,
    };

    // Only add keys that actually exist
    Object.entries(netsKeys).forEach(([key, value]) => {
      if (value) {
        this._keys[key] = value;
      }
    });
  }


  /**
   * Returns the currently loaded keys
   */
  public getKeys(): AuthKeys {
    return this._keys;
  }

  /**
   * Retrieve a key value from the loaded environment keys.
   * @param key - The key to fetch
   * @param options - Optional fallback value or required flag
   * @returns The value of the key, fallback, or empty string
   * @throws If required is true and key is missing or empty
   */
  public getKey(
    key: string,
    { fallback, required }: { fallback?: string; required?: boolean } = {}
  ): string {
    // Check if key exists and is a non-empty string
    const value = this._keys[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }

    // Use fallback if provided
    if (fallback !== undefined) {
      return fallback;
    }

    // Throw error if key is required but missing
    if (required) {
      throw new Error(`Missing required environment key: ${key} for ${this._country}`);
    }

    // Warn in console if key is missing or empty
    console.warn(`Environment key missing or empty: ${key} for ${this._country}`);
    return '';
  }



  /**
   * Checks if a specific payment provider is supported in current country
   */
  public isAuthSupported(provider: 'vipps' | 'nets'): boolean {
    switch (provider) {
      case 'vipps':
        return this._country === 'NO';
      case 'nets':
        return this._country === 'NO' || this._country === 'FI';
      default:
        return false;
    }
  }

  /**
   * Returns the current brand
   */
  public getBrand(): BrandIdentifier {
    return this._brand;
  }

  /**
   * Returns the current country
   */
  public getCountry(): CountryCode {
    return this._country;
  }

  /**
   * Static helper method to check if Vipps is available
   */
  public static isVippsAvailable(): boolean {
    return !!import.meta.env.VITE_VIPPS_CLIENT_ID;
  }

  /**
   * Static helper method to check if Nets is available
   */
  public static isNetsAvailable(): boolean {
    return !!import.meta.env.VITE_NETS_API_OPENID_CONFIG_URL;
  }
}